import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdSettings, MdSave } from 'react-icons/md';
import './MasterData.css';

interface Department {
  departmentId: number;
  departmentName: string;
  departmentCode: string;
  description: string;
  manager: string;
  isActive: string;
}

interface Employee {
  employeeId: number;
  employeeCode: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  department: string;
  position: string;
  tin: string;
  sssNumber: string;
  philhealthNumber: string;
  pagibigNumber: string;
  basicSalary: number;
  email: string;
  mobile: string;
  isActive: string;
}

interface DepartmentManagementModalProps {
  departments: Department[];
  onClose: () => void;
  onSave: (departments: Department[]) => void;
}

// Department Management Modal Component
const DepartmentManagementModal: React.FC<DepartmentManagementModalProps> = ({ departments, onClose, onSave }) => {
  const [depts, setDepts] = useState<Department[]>(departments);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<Department>({
    departmentId: 0,
    departmentName: '',
    departmentCode: '',
    description: '',
    manager: '',
    isActive: 'Y'
  });

  const handleAdd = () => {
    setEditingDept(null);
    setFormData({
      departmentId: 0,
      departmentName: '',
      departmentCode: '',
      description: '',
      manager: '',
      isActive: 'Y'
    });
    setShowAddForm(true);
  };

  const handleEdit = (dept: Department) => {
    setEditingDept(dept);
    setFormData(dept);
    setShowAddForm(true);
  };

  const handleSaveDept = () => {
    if (!formData.departmentName || !formData.departmentCode) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingDept) {
      setDepts(depts.map(d => d.departmentId === formData.departmentId ? formData : d));
    } else {
      setDepts([...depts, { ...formData, departmentId: Date.now() }]);
    }
    setShowAddForm(false);
  };

  const handleDelete = (departmentId: number) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      setDepts(depts.filter(d => d.departmentId !== departmentId));
    }
  };

  const handleSubmit = () => {
    onSave(depts);
    onClose();
  };

  return (
    <div className="modal-overlay nested-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manage Departments</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {!showAddForm ? (
            <>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, color: '#666' }}>Manage company departments</p>
                <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                  <MdAdd /> Add New Department
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Department Name</th>
                      <th>Code</th>
                      <th>Manager</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depts.map(dept => (
                      <tr key={dept.departmentId}>
                        <td className="name">{dept.departmentName}</td>
                        <td className="code">{dept.departmentCode}</td>
                        <td>{dept.manager || '-'}</td>
                        <td>
                          <span className={`badge ${dept.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                            {dept.isActive === 'Y' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon btn-edit" onClick={() => handleEdit(dept)}>
                              <MdEdit />
                            </button>
                            <button className="btn-icon btn-delete" onClick={() => handleDelete(dept.departmentId)}>
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <h4 style={{ marginBottom: '20px', color: '#1a237e' }}>
                {editingDept ? 'Edit Department' : 'Add New Department'}
              </h4>

              <div className="form-group">
                <label>Department Name *</label>
                <input
                  type="text"
                  required
                  value={formData.departmentName}
                  onChange={(e) => setFormData({ ...formData, departmentName: e.target.value })}
                  placeholder="e.g., Production, Sales, Accounting"
                />
              </div>

              <div className="form-group">
                <label>Department Code *</label>
                <input
                  type="text"
                  required
                  value={formData.departmentCode}
                  onChange={(e) => setFormData({ ...formData, departmentCode: e.target.value.toUpperCase() })}
                  placeholder="e.g., PROD, SALES, ACCT"
                />
              </div>

              <div className="form-group">
                <label>Manager</label>
                <input
                  type="text"
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  placeholder="Department manager name"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this department"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}
                >
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveDept}>
                  {editingDept ? 'Update' : 'Add'} Department
                </button>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {!showAddForm && (
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
              <MdSave /> Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    // Initialize departments
    setDepartments([
      { departmentId: 1, departmentName: 'Production', departmentCode: 'PROD', description: 'Manufacturing and production', manager: 'Juan Dela Cruz', isActive: 'Y' },
      { departmentId: 2, departmentName: 'Accounting', departmentCode: 'ACCT', description: 'Finance and accounting', manager: '', isActive: 'Y' },
      { departmentId: 3, departmentName: 'Sales', departmentCode: 'SALES', description: 'Sales and marketing', manager: '', isActive: 'Y' },
      { departmentId: 4, departmentName: 'HR', departmentCode: 'HR', description: 'Human resources', manager: '', isActive: 'Y' }
    ]);
  }, []);

  useEffect(() => {
    // Mock data
    setEmployees([
      {
        employeeId: 1,
        employeeCode: 'EMP001',
        firstName: 'Juan',
        middleName: 'Santos',
        lastName: 'Dela Cruz',
        fullName: 'Juan Santos Dela Cruz',
        department: 'Production',
        position: 'Production Manager',
        tin: '123-456-789-000',
        sssNumber: '12-3456789-0',
        philhealthNumber: '12-345678901-2',
        pagibigNumber: '1234-5678-9012',
        basicSalary: 35000,
        email: 'juan.delacruz@dewfoam.ph',
        mobile: '+63 917 123 4567',
        isActive: 'Y'
      },
      {
        employeeId: 2,
        employeeCode: 'EMP002',
        firstName: 'Maria',
        middleName: 'Garcia',
        lastName: 'Santos',
        fullName: 'Maria Garcia Santos',
        department: 'Accounting',
        position: 'Accountant',
        tin: '234-567-890-000',
        sssNumber: '23-4567890-1',
        philhealthNumber: '23-456789012-3',
        pagibigNumber: '2345-6789-0123',
        basicSalary: 30000,
        email: 'maria.santos@dewfoam.ph',
        mobile: '+63 917 234 5678',
        isActive: 'Y'
      },
      {
        employeeId: 3,
        employeeCode: 'EMP003',
        firstName: 'Pedro',
        middleName: 'Reyes',
        lastName: 'Garcia',
        fullName: 'Pedro Reyes Garcia',
        department: 'Sales',
        position: 'Sales Executive',
        tin: '345-678-901-000',
        sssNumber: '34-5678901-2',
        philhealthNumber: '34-567890123-4',
        pagibigNumber: '3456-7890-1234',
        basicSalary: 25000,
        email: 'pedro.garcia@dewfoam.ph',
        mobile: '+63 917 345 6789',
        isActive: 'Y'
      }
    ]);
  }, []);

  const filteredEmployees = employees.filter(emp =>
    emp.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Employees</h2>
            <p className="subtitle">Manage employee information with Philippine government IDs</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingEmployee(null); setShowModal(true); }}>
            <MdAdd /> Add New Employee
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>TIN</th>
                  <th>SSS</th>
                  <th>PhilHealth</th>
                  <th>Pag-IBIG</th>
                  <th>Basic Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(emp => (
                  <tr key={emp.employeeId}>
                    <td className="code">{emp.employeeCode}</td>
                    <td className="name">{emp.fullName}</td>
                    <td>{emp.department}</td>
                    <td>{emp.position}</td>
                    <td><small>{emp.tin}</small></td>
                    <td><small>{emp.sssNumber}</small></td>
                    <td><small>{emp.philhealthNumber}</small></td>
                    <td><small>{emp.pagibigNumber}</small></td>
                    <td>{formatCurrency(emp.basicSalary)}</td>
                    <td>
                      <span className={`badge ${emp.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                        {emp.isActive === 'Y' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => { setEditingEmployee(emp); setShowModal(true); }}>
                          <MdEdit />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => {
                          if (window.confirm('Delete this employee?')) {
                            setEmployees(employees.filter(e => e.employeeId !== emp.employeeId));
                          }
                        }}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <EmployeeModal
          employee={editingEmployee}
          departments={departments}
          onClose={() => setShowModal(false)}
          onSave={(emp) => {
            if (editingEmployee) {
              setEmployees(employees.map(e => e.employeeId === emp.employeeId ? emp : e));
            } else {
              setEmployees([...employees, { ...emp, employeeId: Date.now() }]);
            }
            setShowModal(false);
          }}
          onUpdateDepartments={(depts) => {
            setDepartments(depts);
          }}
        />
      )}
    </MainLayout>
  );
};

interface EmployeeModalProps {
  employee: Employee | null;
  departments: Department[];
  onClose: () => void;
  onSave: (employee: Employee) => void;
  onUpdateDepartments: (departments: Department[]) => void;
}

const EmployeeModal: React.FC<EmployeeModalProps> = ({ employee, departments, onClose, onSave, onUpdateDepartments }) => {
  const [showDeptManagement, setShowDeptManagement] = useState(false);
  const [formData, setFormData] = useState<Employee>(
    employee || {
      employeeId: 0,
      employeeCode: '',
      firstName: '',
      middleName: '',
      lastName: '',
      fullName: '',
      department: '',
      position: '',
      tin: '',
      sssNumber: '',
      philhealthNumber: '',
      pagibigNumber: '',
      basicSalary: 0,
      email: '',
      mobile: '',
      isActive: 'Y'
    }
  );

  const updateFullName = (first: string, middle: string, last: string) => {
    const fullName = `${first} ${middle} ${last}`.trim();
    setFormData({ ...formData, firstName: first, middleName: middle, lastName: last, fullName });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{employee ? 'Edit Employee' : 'Add New Employee'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div className="modal-body">
            <h4 className="section-heading">Personal Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Employee Code *</label>
                <input type="text" required value={formData.employeeCode}
                  onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })} />
              </div>
            </div>
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>First Name *</label>
                <input type="text" required value={formData.firstName}
                  onChange={(e) => updateFullName(e.target.value, formData.middleName, formData.lastName)} />
              </div>
              <div className="form-group">
                <label>Middle Name</label>
                <input type="text" value={formData.middleName}
                  onChange={(e) => updateFullName(formData.firstName, e.target.value, formData.lastName)} />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input type="text" required value={formData.lastName}
                  onChange={(e) => updateFullName(formData.firstName, formData.middleName, e.target.value)} />
              </div>
            </div>

            <h4 className="section-heading">Government IDs (Required for Payroll)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>TIN (Taxpayer ID) *</label>
                <input type="text" required value={formData.tin} placeholder="XXX-XXX-XXX-XXX"
                  onChange={(e) => setFormData({ ...formData, tin: e.target.value })} />
              </div>
              <div className="form-group">
                <label>SSS Number *</label>
                <input type="text" required value={formData.sssNumber} placeholder="XX-XXXXXXX-X"
                  onChange={(e) => setFormData({ ...formData, sssNumber: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>PhilHealth Number *</label>
                <input type="text" required value={formData.philhealthNumber} placeholder="XX-XXXXXXXXX-X"
                  onChange={(e) => setFormData({ ...formData, philhealthNumber: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Pag-IBIG (HDMF) Number *</label>
                <input type="text" required value={formData.pagibigNumber} placeholder="XXXX-XXXX-XXXX"
                  onChange={(e) => setFormData({ ...formData, pagibigNumber: e.target.value })} />
              </div>
            </div>

            <h4 className="section-heading">Employment Information</h4>
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Department *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Department</option>
                    {departments
                      .filter(dept => dept.isActive === 'Y')
                      .map(dept => (
                        <option key={dept.departmentId} value={dept.departmentName}>
                          {dept.departmentName}
                        </option>
                      ))
                    }
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowDeptManagement(true)}
                    title="Manage Departments"
                    style={{ padding: '8px 12px', minWidth: 'auto' }}
                  >
                    <MdSettings />
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Position *</label>
                <input type="text" required value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Basic Salary *</label>
                <input type="number" step="0.01" required value={formData.basicSalary}
                  onChange={(e) => setFormData({ ...formData, basicSalary: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            <h4 className="section-heading">Contact Information</h4>
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Mobile</label>
                <input type="text" value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}>
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{employee ? 'Update' : 'Create'}</button>
          </div>
        </form>

        {showDeptManagement && (
          <DepartmentManagementModal
            departments={departments}
            onClose={() => setShowDeptManagement(false)}
            onSave={(depts) => {
              onUpdateDepartments(depts);
              setShowDeptManagement(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Employees;
